import { ignore } from '@yp/common/async.ts';
import { log, logCallSite } from '@yp/common/core.ts';
import { isAbsent, isFunction, isPresent } from '@yp/common/identity.ts';
import type { MaybeAnnotated } from '@yp/common/schema/annotations.ts';
import { tick } from 'svelte';
import type { Readable } from 'svelte/store';

const defaultDebug = false;

// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention
const UseObservableErrors = ['no-observable'] as const;
export type UseObservableError = (typeof UseObservableErrors)[number];

export const IgnoreValue = Symbol('IgnoreValue');

export type Derive<T, U = T> = (value: T) => U | typeof IgnoreValue;

export function chain<T, U, V>(first: Derive<T, U>, second?: Derive<U, V>): Derive<T, V> {
  if (isAbsent(second)) {
    return first as unknown as Derive<T, V>;
  }
  return (value) => {
    const intermediate = first(value);
    if (intermediate === IgnoreValue) {
      return IgnoreValue;
    }
    return second(intermediate);
  };
}

export type UseObservableOptions<T, U> = {
  debug?: boolean;
  noObsError?: boolean;
  derived?: Derive<T, U> | undefined;
};

export function useObservable<T, U = T>(
  obs: MaybeAnnotated<Readable<T>> | (() => MaybeAnnotated<Readable<T>> | undefined),
  options?: UseObservableOptions<T, U>,
) {
  const debug = options?.debug ?? defaultDebug;
  const noObsError = options?.noObsError ?? false;
  const derived = options?.derived;
  let value = $state<U>();
  let error = $state<UseObservableError>();
  let loading = $state(true);
  let ctx: string | undefined;
  if (debug) {
    const callSite = logCallSite();
    ctx = isPresent(callSite) ? `${callSite.file}:${callSite.line}` : 'unknown';
  }

  function setState(params?: { value: U } | { error: UseObservableError }) {
    if (isAbsent(params)) {
      value = undefined;
      error = undefined;
      loading = true;
    } else if ('value' in params) {
      value = params.value;
      error = undefined;
      loading = false;
    } else {
      value = undefined;
      error = params.error;
      loading = false;
    }
  }

  function handleObservableChange(o: MaybeAnnotated<Readable<T>> | undefined) {
    if (isAbsent(o)) {
      if (noObsError) {
        setState({ error: 'no-observable' });
      } else {
        setState();
      }
      return undefined;
    }

    if (debug) {
      log('useObservable sub', { obs: o.annotation ?? 'unknown', ctx });
    }
    const unsub = o.subscribe((v) => {
      if (debug) {
        log('useObservable value', { obs: o.annotation ?? 'unknown', v, ctx });
      }
      let value: U;
      if (isPresent(derived)) {
        const result = derived(v);
        if (result === IgnoreValue) {
          if (debug) {
            log('useObservable value ignored', { obs: o.annotation ?? 'unknown', v, ctx });
          }
          return;
        }
        value = result;
      } else {
        value = v as unknown as U;
      }
      setState({ value });
    });

    return () => {
      setState();
      if (debug) {
        log('useObservable unsub', { obs: o.annotation ?? 'unknown', ctx });
      }
      ignore(
        tick().then(() => {
          if (debug) {
            log('useObservable unsub.tick', { obs: o.annotation ?? 'unknown', ctx });
          }
          unsub();
        }),
      );
    };
  }

  $effect(() => handleObservableChange(isFunction(obs) ? obs() : obs));

  return {
    get loading() {
      return loading;
    },
    get v() {
      return value;
    },
    get value() {
      return value;
    },
    get error() {
      return error;
    },
  };
}
